import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { SmallBodyText } from "../../shared/Text";
import Linkify from "react-linkify";
import styled from "styled-components";

const INITIAL_VISIBLE_MESSAGES = 80;
const LOAD_MORE_BATCH_SIZE = 80;
const TOP_SCROLL_THRESHOLD_PX = 20;
const BOTTOM_SCROLL_THRESHOLD_PX = 4;

const linkDecorator = (
  href,
  text,
  key // for links in chat in new tab
) => (
  <a href={href} key={key} target="_blank" rel="noopener noreferrer">
    {text}
  </a>
);

const normalizeTimestamp = (timestamp) => {
  const parsedTimestamp = Number(timestamp);
  return Number.isFinite(parsedTimestamp) ? parsedTimestamp : 0;
};

const normalizeChat = (chat) => ({
  username: chat?.username || "",
  message: chat?.message || chat?.msg || "",
  timestamp: normalizeTimestamp(chat?.timestamp ?? chat?.message_timestamp),
});

const mergeUniqueChats = (olderChats, newerChats) => {
  const seen = new Set();
  const mergedChats = [];

  [...olderChats, ...newerChats].forEach((chat, index) => {
    const normalized = normalizeChat(chat);
    const dedupeKey =
      normalized.timestamp > 0
        ? `${normalized.timestamp}:${normalized.username}:${normalized.message}`
        : `no-timestamp:${index}:${normalized.username}:${normalized.message}`;

    if (!seen.has(dedupeKey)) {
      seen.add(dedupeKey);
      mergedChats.push(normalized);
    }
  });

  return mergedChats.sort((left, right) => left.timestamp - right.timestamp);
};

const ChatMain = ({ chats, updateScroll, fetchOlderChats, chatSourceId }) => {
  const chatMessageContainerRef = useRef(null);
  const loadingOlderRef = useRef(false);
  const pendingScrollRestoreRef = useRef(null);

  const [isScrollBottom, setIsScrollBottom] = useState(true);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_MESSAGES);
  const [loadedOlderChats, setLoadedOlderChats] = useState([]);
  const [hasMoreOnServer, setHasMoreOnServer] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  useEffect(() => {
    setLoadedOlderChats([]);
    setHasMoreOnServer(true);
    setVisibleCount(INITIAL_VISIBLE_MESSAGES);
    setIsLoadingOlder(false);
    loadingOlderRef.current = false;
    pendingScrollRestoreRef.current = null;
  }, [chatSourceId]);

  const allChats = useMemo(
    () => mergeUniqueChats(loadedOlderChats, chats),
    [loadedOlderChats, chats]
  );

  const firstVisibleIndex = Math.max(allChats.length - visibleCount, 0);
  const visibleChats = allChats.slice(firstVisibleIndex);
  const hasHiddenChats = visibleCount < allChats.length;

  useEffect(() => {
    setVisibleCount((currentVisibleCount) => {
      if (isScrollBottom) {
        return Math.min(INITIAL_VISIBLE_MESSAGES, allChats.length);
      }
      return Math.min(
        Math.max(currentVisibleCount, INITIAL_VISIBLE_MESSAGES),
        allChats.length
      );
    });
  }, [allChats.length, isScrollBottom]);

  const scrollToBottom = () => {
    if (chatMessageContainerRef.current) {
      chatMessageContainerRef.current.scrollTop =
        chatMessageContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    const chatMessageContainer = chatMessageContainerRef.current;
    if (!chatMessageContainer) {
      return;
    }

    if (pendingScrollRestoreRef.current) {
      const { previousScrollHeight, previousScrollTop } =
        pendingScrollRestoreRef.current;
      chatMessageContainer.scrollTop =
        chatMessageContainer.scrollHeight -
        previousScrollHeight +
        previousScrollTop;
      pendingScrollRestoreRef.current = null;
      loadingOlderRef.current = false;
      return;
    }

    if (isScrollBottom) {
      scrollToBottom();
    }
  }, [updateScroll, allChats, isScrollBottom, visibleCount]);

  const loadOlderMessages = useCallback(async () => {
    const chatMessageContainer = chatMessageContainerRef.current;
    if (!chatMessageContainer || loadingOlderRef.current) {
      return;
    }

    loadingOlderRef.current = true;
    pendingScrollRestoreRef.current = {
      previousScrollHeight: chatMessageContainer.scrollHeight,
      previousScrollTop: chatMessageContainer.scrollTop,
    };

    if (hasHiddenChats) {
      setVisibleCount((currentVisibleCount) =>
        Math.min(currentVisibleCount + LOAD_MORE_BATCH_SIZE, allChats.length)
      );
      return;
    }

    if (!hasMoreOnServer || !fetchOlderChats || allChats.length === 0) {
      loadingOlderRef.current = false;
      pendingScrollRestoreRef.current = null;
      return;
    }

    const earliestTimestamp = allChats[0].timestamp;
    if (!earliestTimestamp) {
      setHasMoreOnServer(false);
      loadingOlderRef.current = false;
      pendingScrollRestoreRef.current = null;
      return;
    }

    setIsLoadingOlder(true);
    try {
      const { chats: olderChats, hasMore } = await fetchOlderChats(
        earliestTimestamp,
        LOAD_MORE_BATCH_SIZE
      );

      const normalizedOlderChats = (olderChats || []).map(normalizeChat);
      if (normalizedOlderChats.length > 0) {
        setLoadedOlderChats((existingOlderChats) =>
          mergeUniqueChats(normalizedOlderChats, existingOlderChats)
        );
        setVisibleCount(
          (currentVisibleCount) =>
            currentVisibleCount + normalizedOlderChats.length
        );
      } else {
        loadingOlderRef.current = false;
        pendingScrollRestoreRef.current = null;
      }

      setHasMoreOnServer(Boolean(hasMore));
    } catch (_) {
      loadingOlderRef.current = false;
      pendingScrollRestoreRef.current = null;
      setHasMoreOnServer(false);
    } finally {
      setIsLoadingOlder(false);
    }
  }, [allChats, fetchOlderChats, hasHiddenChats, hasMoreOnServer]);

  const onScroll = () => {
    if (!chatMessageContainerRef.current) {
      return;
    }

    const { scrollTop, clientHeight, scrollHeight } =
      chatMessageContainerRef.current;
    setIsScrollBottom(
      scrollTop + clientHeight >= scrollHeight - BOTTOM_SCROLL_THRESHOLD_PX
    );

    if (scrollTop <= TOP_SCROLL_THRESHOLD_PX) {
      loadOlderMessages();
    }
  };

  return (
    <ChatMessageContainer
      onScroll={onScroll}
      ref={chatMessageContainerRef}
      id="chat-msg-container"
    >
      {(hasHiddenChats || hasMoreOnServer || isLoadingOlder) && (
        <MoreMessagesText>
          {isLoadingOlder
            ? "Loading older messages..."
            : "Scroll up to load older messages"}
        </MoreMessagesText>
      )}
      {visibleChats.map((chat, i) => {
        const chatIndex = firstVisibleIndex + i;
        return (
          <ChatMessage
            key={`${chat.timestamp || "chat"}-${chatIndex}`}
            align="left"
            isGameMsg={!chat.username}
            id={`chat-${chatIndex}`}
          >
            <strong>{chat.username ? `${chat.username}: ` : ""}</strong>
            <Linkify componentDecorator={linkDecorator}>{chat.message}</Linkify>
          </ChatMessage>
        );
      })}
    </ChatMessageContainer>
  );
};

const ChatMessageContainer = styled.div`
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  overflow-wrap: break-word;
  flex: auto;
  color: ${({ theme }) => theme.text.mainBody};
`;

const ChatMessage = styled(SmallBodyText)`
  padding: 0 20px;
  margin: 8px 0;
  display: block;
  ${(props) =>
    props.isGameMsg &&
    props.theme &&
    `
    color: ${props.theme.chat.gameText};
    font-weight: bold;
  `}
  a, a:hover {
    color: ${({ theme }) => theme.chat.linkText};
    text-decoration: underline;
  }
`;

const MoreMessagesText = styled(SmallBodyText)`
  position: sticky;
  top: 0;
  text-align: center;
  margin: 0;
  padding: 8px 0;
  background: ${({ theme }) => theme.chat.background};
  color: ${({ theme }) => theme.text.mainBody};
  opacity: 0.8;
`;

export default ChatMain;
